@file:OptIn(ExperimentalTime::class, ExperimentalMaterial3Api::class)

package com.wintermar.inspection.core.ui.component.datetime

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.DatePicker
import androidx.compose.material3.DatePickerDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.TimeInput
import androidx.compose.material3.TimePickerDefaults
import androidx.compose.material3.rememberDatePickerState
import androidx.compose.material3.rememberTimePickerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.focusProperties
import com.wintermar.inspection.core.ui.component.TextBodyRegular
import com.wintermar.inspection.core.ui.component.button.Button
import com.wintermar.inspection.core.ui.component.button.variant.ButtonVariant
import com.wintermar.inspection.core.ui.component.datetime.util.DateTimeUtil
import com.wintermar.inspection.core.ui.component.datetime.util.PastAndCurrentDates
import com.wintermar.inspection.core.ui.theme.AppDimension
import com.wintermar.inspection.core.ui.theme.LocalAppTheme
import com.wintermar.inspection.core.ui.theme.color.model.ThemeColor
import kotlinx.coroutines.delay
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

@Composable
fun DateTimePicker(
    isoUtc0String: String = "", //example value: 1970-01-01T00:00:00.000Z
    modifier: Modifier = Modifier,
    color: ThemeColor = LocalAppTheme.current.color,
    dimension: AppDimension = LocalAppTheme.current.dimension,
    onValueChanged: (String) -> Unit,
) {
    val localTimeZone = TimeZone.currentSystemDefault()
    val now = remember { Clock.System.now().toLocalDateTime(localTimeZone) }

    val initialLocalDateTime = remember(isoUtc0String, now) {
        if (isoUtc0String.isNotBlank()) {
            Instant.parse(isoUtc0String).toLocalDateTime(localTimeZone)
        } else {
            now
        }
    }

    val datePickerState = rememberDatePickerState(
        initialSelectedDateMillis = initialLocalDateTime.date.atStartOfDayIn(TimeZone.UTC)
            .toEpochMilliseconds(),
        selectableDates = PastAndCurrentDates
    )

    val timePickerState = rememberTimePickerState(
        initialHour = initialLocalDateTime.hour,
        initialMinute = initialLocalDateTime.minute
    )

    var allowFocus by remember { mutableStateOf(false) }
    val defaultModifier = Modifier.fillMaxWidth().padding(horizontal = dimension.spacing.space4)

    // Enable focus after composition is complete to prevent autofocus
    LaunchedEffect(Unit) {
        // Wait for composition to complete before allowing focus
        delay(200)
        allowFocus = true
    }

    // Observe changes to date or time and emit formatted ISO via onValueChanged
    LaunchedEffect(
        datePickerState.selectedDateMillis,
        timePickerState.hour,
        timePickerState.minute
    ) {
        val iso = DateTimeUtil
            .getIsoUtc0DateTimeString(
                dateState = datePickerState,
                timeState = timePickerState
            )
        onValueChanged(iso ?: "")
    }

    Column(
        modifier = modifier.fillMaxWidth()
    ) {
        DatePicker(
            state = datePickerState,
            title = null,
            headline = null,
            colors = DatePickerDefaults.colors().copy(
                containerColor = color.surface.normal,
            ),
            showModeToggle = false
        )
        HorizontalDivider(modifier = defaultModifier, color = color.border.subtle)

        Column(modifier = defaultModifier) {
            TextBodyRegular(
                text = "Time",
                color = { it.default },
                modifier = Modifier.padding(vertical = dimension.spacing.space2)
            )
        }

        Row(
            modifier = defaultModifier,
            horizontalArrangement = Arrangement.spacedBy(
                dimension.spacing.space3
            ),
            verticalAlignment = Alignment.CenterVertically
        ) {
            TimeInput(
                state = timePickerState,
                modifier = Modifier.focusProperties {
                    // Prevent focus during initial composition, allow it afterward
                    canFocus = allowFocus
                },
                colors = TimePickerDefaults.colors().copy(
                    periodSelectorSelectedContainerColor = color.surface.brand.primary.default,
                    periodSelectorSelectedContentColor = color.text.onBrand,
                    periodSelectorBorderColor = color.border.subtle,
                    selectorColor = color.surface.brand.primary.default,
                    timeSelectorSelectedContentColor = color.text.default,
                    timeSelectorUnselectedContentColor = color.text.default,
                    timeSelectorSelectedContainerColor = color.surface.normal,
                    timeSelectorUnselectedContainerColor = color.surface.normal
                )
            )

            Column(
                modifier = Modifier.padding(bottom = dimension.spacing.space6)
            ) {
                Button(
                    buttonVariant = ButtonVariant.MediumGhost,
                    text = "Now",
                ) {
                    timePickerState.hour = now.hour
                    timePickerState.minute = now.minute
                }
            }
        }
    }
}
